// src/pages/AvailabilityPage.jsx
import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import "@bitnoi.se/react-scheduler/dist/style.css";
import { Scheduler } from "@bitnoi.se/react-scheduler";
import {
    Box,
    TextField,
    Button,
    CircularProgress,
    Typography,
    Modal,
    Card,
    CardContent,
} from "@mui/material";

// Darker color palette for different event types
const TYPE_COLORS = {
    quote: "#0097a7",
    activity: "#fbc02d",
    job: "#388e3c",
};
// Abbreviated labels for event types
const TYPE_CODES = { quote: "Q", activity: "A", job: "J" };
// Palette for row backgrounds
const ROW_PALETTE = [
    "#f0f4c3",  // light lime
    "#bbdefb",  // light blue
    "#ffe0b2",  // light orange
    "#d1c4e9",  // light purple
    "#c8e6c9",  // light green
];

export default function AvailabilityPage() {
    const today = new Date().toISOString().slice(0, 10);
    const [startStr, setStartStr] = useState(today);
    const [endStr, setEndStr] = useState(today);
    const [rawEvents, setRawEvents] = useState([]);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [selectedEvent, setSelectedEvent] = useState(null);

    // Stub to fetch detailed quote by reference
    const fetchQuote = async (reference) => {
        console.log("🔍 fetching quote for ref:", reference);
    };
    const handleTileClick = (tile) => { setSelectedEvent(tile); fetchQuote(tile.subtitle); };

    const fetchAvailability = useCallback(async () => {
        if (!startStr || !endStr) return;
        setLoading(true);
        try {
            const { data: raw } = await axios.get(
                `https://adam.app.autofront.com.au/api/availability?start=${startStr}&end=${endStr}`
            );
            setRawEvents(raw);

            // Group by staffId
            const byStaff = raw.reduce((acc, ev) => {
                const sid = ev.staffId.toString();
                if (!acc[sid]) acc[sid] = { id: sid, name: ev.staffName, events: [] };
                acc[sid].events.push(ev);
                return acc;
            }, {});

            // Build rows and assign each an index-based color
            const ROW_COLORS = {};
            const rows = Object.values(byStaff).map((r, idx) => {
                ROW_COLORS[r.id] = ROW_PALETTE[idx % ROW_PALETTE.length];
                return {
                    id: r.id,
                    label: { title: r.name },
                    data: r.events.map(ev => {
                        const start = new Date(ev.start);
                        const end = new Date(ev.end);
                        return {
                            id: `${r.id}-${start.toISOString()}`,
                            startDate: start,
                            endDate: end,
                            occupancy: (end - start) / 1000,
                            title: TYPE_CODES[ev.type] || "?",
                            subtitle: ev.reference,
                            description: ev.type,
                            bgColor: TYPE_COLORS[ev.type] || "#555",
                        };
                    }),
                };
            });

            setData({ rows, rowColors: ROW_COLORS });
        } catch (err) {
            console.error("Failed to load availability:", err);
            setData({ rows: [], rowColors: {} });
        } finally {
            setLoading(false);
        }
    }, [startStr, endStr]);

    useEffect(() => { fetchAvailability(); }, [fetchAvailability]);

    // Unpack data and colors
    const rows = data.rows || [];
    const rowColors = data.rowColors || {};

    return (
        <Box sx={{ p: 2 }}>
            {/* Legend & Filters */}
            <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2, mb: 2 }}>
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    {Object.entries(TYPE_COLORS).map(([type, color]) => (
                        <Box key={type} sx={{ display: "flex", alignItems: "center" }}>
                            <Box sx={{ width: 16, height: 16, bgcolor: color, border: "1px solid #999", mr: 0.5, borderRadius: 1 }} />
                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{type}</Typography>
                        </Box>
                    ))}
                </Box>

                <TextField label="From" type="date" value={startStr} onChange={e => setStartStr(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
                <TextField label="To" type="date" value={endStr} onChange={e => setEndStr(e.target.value)} InputLabelProps={{ shrink: true }} size="small" />
                <Button variant="contained" onClick={fetchAvailability}>Load</Button>
            </Box>

            {/* Scheduler */}
            <Box sx={{ position: "relative", height: "75vh", width: "100%" }}>
                {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}><CircularProgress /></Box>
                ) : (
                    rows.length ? (
                        <Scheduler
                            data={rows}
                            isLoading={false}
                            onTileClick={handleTileClick}
                            onItemClick={row => console.log("Row clicked:", row)}
                            config={{
                                zoom: 2,
                                showWeekends: false,
                                showTime: true,
                                showDate: true,
                                maxRecordsPerPage: 6,
                                showLegend: true,
                                rowHeight: 80,
                                tileRenderer: ({ item }) => (
                                    <div style={{ background: item.bgColor, border: item.description === "quote" ? "2px solid #006064" : "2px solid #333", borderRadius: 4, height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
                                        {item.title}
                                    </div>
                                ),
                                // custom rowRenderer to apply background colors per person
                                rowRenderer: ({ row, children }) => (
                                    <div style={{ backgroundColor: rowColors[row.id] || 'transparent' }}>
                                        {children}
                                    </div>
                                )
                            }}
                        />
                    ) : (
                        <Typography>No availability to display.</Typography>
                    )
                )}
            </Box>

            {/* Detail Modal */}
            <Modal open={!!selectedEvent} onClose={() => setSelectedEvent(null)}>
                <Box sx={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 300, bgcolor: "background.paper", boxShadow: 24, p: 2, borderRadius: 1 }}>
                    {selectedEvent && (
                        <Card elevation={0}>
                            <CardContent>
                                <Typography variant="h6">{selectedEvent.title}</Typography>
                                <Typography variant="subtitle2" gutterBottom>{selectedEvent.subtitle}</Typography>
                                <Typography variant="body2" gutterBottom>{selectedEvent.description}</Typography>
                                <Typography variant="body2">{selectedEvent.startDate.toLocaleString()} → {selectedEvent.endDate.toLocaleString()}</Typography>
                            </CardContent>
                        </Card>
                    )}
                </Box>
            </Modal>
        </Box>
    );
}
